import * as Sentry from "@sentry/browser";
import moment from "moment";
import { Action } from "redux";
import { ThunkAction } from "redux-thunk";
import {
  BookingInquiryMessage,
  Chat,
  estimatePrice,
  Inquiry,
  Message,
  NewInquiry,
  Price,
  TextMessage,
  User,
  validateInquiry,
  validateTextMessage,
  Venue
} from "sweet-global";
import firebase from "sweet-web/firebase";
import { CancelBooking, CreateBooking } from "sweet-web/redux/booking/actions";
import * as selectors from "sweet-web/redux/selectors";
import { RootState } from "../index";
import {
  ADD_CHAT_MESSAGES,
  ChatActionTypes,
  CREATE_CHAT_FAILURE,
  CREATE_CHAT_INIT,
  CREATE_CHAT_SUCCESS,
  DELETE_CHAT,
  GET_CHATS_FAILURE,
  GET_CHATS_INIT,
  GET_CHATS_SUCCESS,
  REMOVE_CHAT_MESSAGE,
  RESET_CHAT,
  SEND_MESSAGE_FAILURE,
  SEND_MESSAGE_INIT,
  SEND_MESSAGE_SUCCESS
} from "./types";

const firestore = firebase.firestore();

export const resetChat = (): ChatActionTypes => {
  return { type: RESET_CHAT };
};

export const getChatsInit = (): ChatActionTypes => {
  return { type: GET_CHATS_INIT };
};

export const getChatsSuccess = (
  chats: { [chatId: string]: Chat },
  archivedChats: { [chatId: string]: Chat },
  chatUsers?: { [userId: string]: User },
  chatVenues?: { [venueId: string]: Venue }
): ChatActionTypes => {
  return {
    type: GET_CHATS_SUCCESS,
    chats,
    archivedChats,
    chatUsers,
    chatVenues
  };
};

export const getChatsFailure = (error: Error): ChatActionTypes => {
  return { type: GET_CHATS_FAILURE, error };
};

export const createChatInit = (): ChatActionTypes => {
  return { type: CREATE_CHAT_INIT };
};

export const createChatSuccess = (): ChatActionTypes => {
  return { type: CREATE_CHAT_SUCCESS };
};

export const createChatFailure = (error: Error): ChatActionTypes => {
  return { type: CREATE_CHAT_FAILURE, error };
};

export const deleteChat = (chatId: string): ChatActionTypes => {
  return { type: DELETE_CHAT, chatId };
};

export const sendMessageInit = (): ChatActionTypes => {
  return { type: SEND_MESSAGE_INIT };
};

export const sendMessageSuccess = (): ChatActionTypes => {
  return { type: SEND_MESSAGE_SUCCESS };
};

export const sendMessageFailure = (error: Error): ChatActionTypes => {
  return { type: SEND_MESSAGE_FAILURE, error };
};

export const addChatMessages = (
  chatId: string,
  messages: {
    [messageId: string]: Message;
  }
): ChatActionTypes => {
  return { type: ADD_CHAT_MESSAGES, chatId, messages };
};

export const removeChatMessage = (
  chatId: string,
  messageId: string
): ChatActionTypes => {
  return { type: REMOVE_CHAT_MESSAGE, chatId, messageId };
};

export const GetChats = (): ThunkAction<
  Promise<Function>,
  RootState,
  null,
  Action<string>
> => async (dispatch, getState) => {
  try {
    dispatch(getChatsInit());

    const state = getState();
    const uid = selectors.uid(state);

    if (!uid) throw new Error("no authorized user");
    const userType = selectors.userType(state);
    const userVenues = selectors.userVenues(state);

    const chats: { [chatId: string]: Chat } = {};
    const archivedChats: { [chatId: string]: Chat } = {};
    const chatUsers: { [userId: string]: User } = {};
    const chatVenues: { [venueId: string]: Venue } = {};
    const chatListeners: {
      [listenerId: string]: () => void;
    } = {};

    const processSnapshot = async (snap: firebase.firestore.QuerySnapshot) => {
      const docChanges = snap.docChanges();

      for (const change of docChanges) {
        if (change.type === "added") {
          const doc = change.doc;
          const chat = {
            id: doc.id,
            ...doc.data()
          } as Chat;
          if (chat.active) chats[doc.id] = chat;
          else archivedChats[doc.id] = chat;

          // subscribe to messages
          chatListeners[doc.id] = firestore
            .collection("chats")
            .doc(doc.id)
            .collection("messages")
            .onSnapshot(snap => {
              const changes = snap.docChanges();
              const messages: { [messageId: string]: Message } = {};
              changes.forEach(change => {
                if (change.type === "added") {
                  messages[change.doc.id] = change.doc.data() as Message;
                }
                if (change.type === "modified") {
                  messages[change.doc.id] = change.doc.data() as Message;
                }
                if (change.type === "removed") {
                  dispatch(removeChatMessage(doc.id, change.doc.id));
                }
              });
              if (Object.keys(messages).length > 0 && chat.active)
                dispatch(addChatMessages(doc.id, messages));
            });

          // if user is a venue, get user entities that have engaged with you
          if (userType !== "USER") {
            const userDoc = await firestore
              .collection("users")
              .doc(chat.user)
              .get();
            if (userDoc.exists) {
              const chatUser = {
                id: userDoc.id,
                ...userDoc.data()
              } as User;

              chatUsers[userDoc.id] = chatUser;
            }
          }

          // if normal user, get venues that have engaged with you
          if (userType === "USER") {
            const venueDoc = await firestore
              .collection("venues")
              .doc(chat.venue)
              .get();
            if (venueDoc.exists) {
              const chatVenue = {
                id: venueDoc.id,
                ...venueDoc.data()
              } as Venue;

              chatVenues[venueDoc.id] = chatVenue;
            }
          }
        }
        if (change.type === "modified") {
          const doc = change.doc;
          const chat = {
            ...doc.data(),
            id: doc.id
          } as Chat;

          // remove chat if deactivated and add it if activated
          if (!chat.active) {
            if (chats[doc.id]) delete chats[doc.id];
            if (!archivedChats[doc.id]) {
              archivedChats[doc.id] = chat;
            }
          } else if (chat.active) {
            if (!chats[doc.id]) chats[doc.id] = chat;
            if (archivedChats[doc.id]) delete archivedChats[doc.id];
          }
        }
        if (change.type === "removed") {
          const doc = change.doc;
          if (chats[doc.id]) delete chats[doc.id];
          if (archivedChats[doc.id]) delete chats[doc.id];
          dispatch(deleteChat(doc.id));
        }
      }

      dispatch(getChatsSuccess(chats, archivedChats, chatUsers, chatVenues));
    };

    if (
      userType === "VENUE" &&
      typeof userVenues === "object" &&
      userVenues.length > 0
    ) {
      for (const venueId of userVenues) {
        try {
          chatListeners[venueId] = firestore
            .collection("chats")
            .where("venue", "==", venueId)
            .onSnapshot(processSnapshot);
          chatListeners.sweet = firestore
            .collection("chats")
            .where("user", "==", uid)
            .onSnapshot(processSnapshot);
        } catch (error) {
          Sentry.captureException(error);
          console.log(error);
        }
      }
    } else if (userType === "ADMIN") {
      chatListeners.admin = firestore
        .collection("chats")
        .where("sweet", "==", true)
        .onSnapshot(processSnapshot);
    } else if (userType === "USER") {
      try {
        chatListeners.user = firestore
          .collection("chats")
          .where("user", "==", state.auth.uid)
          .onSnapshot(processSnapshot);
      } catch (error) {
        Sentry.captureException(error);
        console.log(error);
      }
    }

    return () => {
      Object.values(chatListeners).forEach(listener => {
        if (typeof listener === "function") listener();
      });
    };
  } catch (error) {
    Sentry.captureException(error);
    console.log(error);
    dispatch(getChatsFailure(error));

    return () => {};
  }
};

export const CreateChat = (
  venue: string,
  newInquiry?: NewInquiry
): ThunkAction<
  Promise<string | null>,
  RootState,
  null,
  Action<string>
> => async (dispatch, getState) => {
  try {
    dispatch(createChatInit());

    const state = getState();

    const uid = selectors.uid(state);
    const chats = selectors.chats(state);

    if (!uid) throw new Error("no authorized user");

    // check if user already has active chat with this venue
    let chatId: string = null;
    Object.keys(chats).forEach(existingChatId => {
      const chat = selectors.chat(existingChatId)(state);
      if (chat.venue === venue && chat.active) chatId = existingChatId;
    });

    if (!chatId) {
      // create chat if user doesn't already have one with this venue
      const chatData: Chat = {
        active: true,
        user: state.auth.uid,
        createdTime: moment().unix(),
        venue
      };

      const newDoc = await firestore.collection("chats").add(chatData);
      chatId = newDoc.id;
      await GetChats()(dispatch, getState, null);
    }

    const venueEntity = selectors.getVenue(venue)(state);

    if (newInquiry) {
      const inquiry: Inquiry = {
        ...newInquiry,
        status: "INQUIRY",
        chat: chatId,
        user: uid,
        venue,
        time: moment().unix(),
        estimatedPrice: estimatePrice(venueEntity, newInquiry)
      };

      // create booking from inquiry
      const inquiryValidation = validateInquiry(inquiry);

      if (!inquiryValidation.valid)
        throw new Error(`Invalid inquiry – please fill out all fields`);

      await CreateBooking(inquiry)(dispatch, getState, null);
    }

    // add booking to chat
    dispatch(createChatSuccess());

    return chatId;
  } catch (error) {
    Sentry.captureException(error);
    dispatch(createChatFailure(error));
    throw error;
  }
};

export const ArchiveChat = (
  chatId: string
): ThunkAction<Promise<void>, RootState, null, Action<string>> => async (
  dispatch,
  getState
) => {
  try {
    const state = getState();
    const chat = selectors.chat(chatId)(state);

    // cancel bookings
    if (chat.booking) {
      await CancelBooking(chat.booking)(dispatch, getState, null);
    }

    // deactivate chat
    await firestore
      .collection("chats")
      .doc(chatId)
      .update({
        active: false
      });
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};

export const DeleteChat = (
  chatId: string
): ThunkAction<Promise<void>, RootState, null, Action<string>> => async (
  dispatch,
  getState
) => {
  try {
    const state = getState();
    const chat = selectors.chat(chatId)(state);

    // mark all bookings related to chat cancelled
    if (chat.booking) {
      await firestore
        .collection("bookings")
        .doc(chat.booking)
        .update({
          status: "CANCELLED"
        });
    }

    // delete chat
    await firestore
      .collection("chats")
      .doc(chatId)
      .delete();

    dispatch(deleteChat(chatId));
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};

export const SendMessage = (
  message: string,
  chatId: string
): ThunkAction<
  Promise<string | null>,
  RootState,
  null,
  Action<string>
> => async (dispatch, getState) => {
  try {
    dispatch(sendMessageInit());

    const state = getState();

    if (!state.auth.uid) throw new Error("no authorized user");

    const messageData = {
      type: "MESSAGE",
      message,
      time: moment().unix(),
      from: state.auth.uid,
      seen: false
    } as TextMessage;

    const messageValidation = validateTextMessage(messageData);
    if (!messageValidation.valid) throw new Error(`Invalid message`);

    // add message
    const newDoc = await firestore
      .collection("chats")
      .doc(chatId)
      .collection("messages")
      .add(messageData);

    dispatch(sendMessageSuccess());

    return newDoc.id;
  } catch (error) {
    Sentry.captureException(error);
    dispatch(sendMessageFailure(error));
    throw error;
  }
};
